import React, { Component, useEffect, Fragment, useMemo, } from 'react';
import { useToggle, useAsync, } from 'react-use';
import { get, sum, mapValues, zip, sumBy, groupBy, keyBy, sortBy, omit, } from 'lodash';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import Select from 'react-select';
import qs from 'qs';
import classnames from 'classnames';
import { format as formatDate } from 'date-fns';

import firebase, { functions } from '../../firebase';
import { getDocumentData, } from '../../shared/firebase';
import { integerFormat, } from '../../util';
import { fiscalYearOfPeriod, } from '../../utils';
import { computeAlerts, generateRows, rowsForExport, buildHeaderTree, computeExchangedItems, } from '../../shared/lib/individualSs';
import { presetConsolidationAccountItems, } from '../../shared/models/consolidationAccountItem';
import { batchCloneFields, } from '../../shared/models/individualSsItem';
import { accountItemCategories, } from '../../shared/config';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import useSingleAmounts from '../hooks/useSingleAmounts';
import RelatedCompanyPage from '../hocs/RelatedCompanyPage';
import HelpLink from '../HelpLink';
import ModelFormModal from '../modals/ModelFormModal';
import SsItemFormModal from '../modals/SsItemFormModal';
import CompanySyncButton from '../CompanySyncButton';
import TrialsSyncButton from '../TrialsSyncButton';
import ModalButton from '../ModalButton';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ExportButton from '../ExportButton';
import QuerySelector from '../QuerySelector';
import WithLoading from '../WithLoading';
import Alerts from '../Alerts';
import Field from '../Field';
import AmountWithExchange from '../AmountWithExchange';

const { entries } = Object;
const { abs } = Math;
const accountItemCategoriesByName = keyBy(accountItemCategories, 'name');

export default RelatedCompanyPage(function CompanyIndividualSs (props) {
  const { company, role, period, yearMonth, relatedCompany, subsidiaryId, history, location, prevEndYearMonth, prevYearMonths, filteredYearMonths, isLockedMonth, periodOptions, } = props;
  const periodSettingRef = company.ref.collection('periodSettings').doc(period);
  const periodSetting = useDocumentSubscription(periodSettingRef, [period]);
  const consolidationAccountItems = useCollectionSubscription(company.ref.collection('accountItems').where('subsidiaryId', '==', null), [company]);
  const allConsolidationAccountItems = useMemo(() => [...consolidationAccountItems, ...presetConsolidationAccountItems], [consolidationAccountItems]);
  const accountItems = useCollectionSubscription(company.ref.collection('accountItems').where('subsidiaryId', '==', subsidiaryId), [subsidiaryId]);
  const ssItemRef = company.ref.collection('individualSsItems').doc([relatedCompany.id, yearMonth].join('__'));
  const ssItem = useDocumentSubscription(ssItemRef, [relatedCompany.id, yearMonth]);
  const exchangedItemRef = company.ref.collection('exchangedItems').doc([relatedCompany.id, yearMonth].join('__'));
  const { items: prevYearItems, } = useSingleAmounts(company, relatedCompany, subsidiaryId, allConsolidationAccountItems, accountItems, prevYearMonths, false, [ssItem]);
  const { items, } = useSingleAmounts(company, relatedCompany, subsidiaryId, allConsolidationAccountItems, accountItems, filteredYearMonths, false, [ssItem]);

  const rows = useMemo(_ => items && prevYearItems && ssItem && generateRows(items, prevYearItems, ssItem), [items, prevYearItems, ssItem]);
  const alerts = useMemo(_ => items && prevYearItems && computeAlerts(items, prevYearItems, ssItem), [items, prevYearItems, ssItem]);
  const { headerRows, columnHeaders, maxDepth } = useMemo(_ => items ? buildHeaderTree(items) : { headerRows: [], columnHeaders: [], maxDepth: 0 }, [items]);

  // 純資産の科目リストを抽出（フォーム用）- columnHeadersから科目のみ抽出してヘッダーと同じ順序にする
  const netAssetAccountItems = useMemo(_ => columnHeaders
    .filter(_ => _.type === 'item')
    .map(_ => _.item)
  , [columnHeaders]);
  const onSubmitBatchClone = async (values, { onClickClose }) => {
    if(!window.confirm('本当にコピーしますか？')) return;

    const { yearMonth: sourceYearMonth, } = values;
    const source = await getDocumentData(company.ref.collection('individualSsItems').doc([relatedCompany.id, sourceYearMonth].join('__')));
    await ssItemRef.set({ ...omit(source, ['id', 'ref']), });
    toast.success('コピーしました');
    onClickClose();
  };
  useEffect(() => {
    if(ssItem === null) {
      ssItemRef.set({ createdAt: new Date(), });
    }
  }, [ssItem]);

  return props.translate(
    <div className="company-individual-ss">
      <WithLoading loading={rows == null}>
        <Alerts alerts={alerts} />
        <div className="mb-3 d-flex justify-content-start align-items-end gap-1">
          <ExportButton screenName="個別SS" targetCompany={relatedCompany} rows={rowsForExport(items, prevYearItems, ssItem)} />
          <EditButton
            itemRef={ssItemRef}
            FormModal={SsItemFormModal}
            formProps={{ currency: relatedCompany.currency, accountItems: netAssetAccountItems }}
            disabled={isLockedMonth}
            onFinish={async (savedValues) => {
              const newSsItem = { ...ssItem, ...savedValues };
              const newItems = computeExchangedItems(items, prevYearItems, newSsItem);
              const newItemsById = keyBy(newItems, 'accountItemId');
              const existingExchangedItem = await getDocumentData(exchangedItemRef);
              const existingItems = existingExchangedItem?.items || [];
              const mergedItems = [
                ...existingItems.filter(_ => !newItemsById[_.accountItemId]),
                ...newItems,
              ];
              await exchangedItemRef.set({ items: mergedItems, yearMonth, subsidiaryId }, { merge: true });
            }}
          />
          <ModalButton label="他の年月からコピー" Modal={ModelFormModal} modalProps={{ values: { period, yearMonth, }, fields: batchCloneFields({ periodOptions, company, fiscalYearOfPeriod, }), title: '他の年月からコピー', onSubmit: onSubmitBatchClone, submitLabel: 'コピー', }} disabled={isLockedMonth} />
        </div>
        <div className="d-flex overflow-auto">
          <table className="table flex-fill table-bordered">
            <thead className="thead-light text-center">
              {
                headerRows.map((rowHeaders, rowIndex) => (
                  <tr key={rowIndex}>
                    {rowIndex === 0 && <th rowSpan={maxDepth + 1} style={{ minWidth: 200 }}></th>}
                    {
                      rowHeaders.map((header, i) => (
                        <th
                          key={i}
                          colSpan={header.colspan}
                          rowSpan={header.rowspan}
                          style={{ minWidth: header.type === 'item' || header.type === 'categoryTotal' ? 100 : undefined, fontWeight: header.type === 'item' ? 'normal' : 'bold' }}
                        >
                          {header.label}
                        </th>
                      ))
                    }
                  </tr>
                ))
              }
            </thead>
            <tbody>
              {
                rows?.map((row, i) => {
                  const { level, label, columns, } = row;
                  const currency = relatedCompany.currency || 'jpy';
                  return (
                    <tr key={i}>
                      <th style={{ textIndent: (level - 1) * 18, fontWeight: level === 1 ? 'bold' : 'normal' }}>{label}</th>
                      {
                        columns.map((column, i) => {
                          const { value, invalid, exchangedAmount, localCurrencyOnly } = column;
                          const isTotalColumn = columnHeaders[i]?.type === 'categoryTotal';
                          return (
                            <td key={i} className={classnames('text-right', { 'table-danger': invalid })} style={{ fontWeight: isTotalColumn ? 'bold' : 'normal' }}>
                              {value === '' ? '' : (
                                <AmountWithExchange currency={localCurrencyOnly ? currency : (exchangedAmount != null ? currency : 'jpy')} amounts={[value, exchangedAmount || 0]} localCurrencyOnly={localCurrencyOnly} />
                              )}
                            </td>
                          );
                        })
                      }
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </WithLoading>
    </div>
  );
});
